import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PageProcessService } from '../../services/page-process.service';
import { AuthService } from '../../services/auth.service';
import { PageProcess } from '../../models';
import { ConfirmDialogComponent } from '../shared/confirm-dialog.component';

@Component({
  selector: 'app-page-processes',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDialogModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <div class="processes-container">
      <div class="page-header">
        <div class="header-content">
          <h1>Procesos de Páginas</h1>
          <div class="header-actions">
            <button mat-raised-button color="primary" (click)="createMonthlyAudits()"
                    [disabled]="executing">
              <mat-icon *ngIf="!executing">add_circle</mat-icon>
              <mat-spinner *ngIf="executing" diameter="20" mode="indeterminate"></mat-spinner>
              <span class="button-text">
                <ng-container *ngIf="!executing">Crear Auditorías Mensuales</ng-container>
                <ng-container *ngIf="executing">Creando...</ng-container>
              </span>
            </button>
            <button mat-raised-button color="primary" (click)="executeAllAudits()"
                    [disabled]="executing">
              <mat-icon *ngIf="!executing">play_arrow</mat-icon>
              <mat-spinner *ngIf="executing" diameter="20" mode="indeterminate"></mat-spinner>
              <span class="button-text">
                <ng-container *ngIf="!executing">Ejecutar Auditorías</ng-container>
                <ng-container *ngIf="executing">Ejecutando...</ng-container>
              </span>
            </button>
          </div>
        </div>
      </div>

      <mat-tab-group [(selectedIndex)]="selectedTab" (selectedIndexChange)="onTabChange($event)">
        <mat-tab label="Por Página">
          <mat-card class="table-container" *ngIf="selectedProcesses.length > 0">
            <table mat-table [dataSource]="selectedProcesses" class="processes-table">
              <ng-container matColumnDef="pageName">
                <th mat-header-cell *matHeaderCellDef>Página</th>
                <td mat-cell *matCellDef="let process">
                  <div class="page-cell">
                    <mat-icon>language</mat-icon>
                    <span>{{ process.pageName }}</span>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="month">
                <th mat-header-cell *matHeaderCellDef>Mes/Año</th>
                <td mat-cell *matCellDef="let process">
                  <span class="month-badge">{{ getMonthName(process.mes) }} {{ process.anio }}</span>
                </td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Estado</th>
                <td mat-cell *matCellDef="let process">
                  <span class="status-badge" [ngClass]="getStatusClass(process.status)">
                    {{ getStatusLabel(process.status) }}
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="processType">
                <th mat-header-cell *matHeaderCellDef>Tipo</th>
                <td mat-cell *matCellDef="let process">
                  <span class="type-badge">{{ getProcessTypeLabel(process.processType) }}</span>
                </td>
              </ng-container>

              <ng-container matColumnDef="createdAt">
                <th mat-header-cell *matHeaderCellDef>Creado</th>
                <td mat-cell *matCellDef="let process">
                  {{ process.createdAt | date: 'dd/MM/yyyy HH:mm' }}
                </td>
              </ng-container>

              <ng-container matColumnDef="updatedAt">
                <th mat-header-cell *matHeaderCellDef>Actualizado</th>
                <td mat-cell *matCellDef="let process">
                  {{ process.updatedAt | date: 'dd/MM/yyyy HH:mm' }}
                </td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>Acciones</th>
                <td mat-cell *matCellDef="let process">
                  <button mat-icon-button (click)="viewAuditResult(process)"
                          matTooltip="Ver reporte"
                          [disabled]="process.status !== 3 || !process.result">
                    <mat-icon>visibility</mat-icon>
                  </button>
                  <button mat-icon-button (click)="sendAuditReport(process)"
                          matTooltip="Enviar por correo"
                          [disabled]="process.status !== 3 || !process.result || sendingEmail">
                    <mat-icon>mail</mat-icon>
                  </button>
                  <button mat-icon-button (click)="deleteProcess(process)"
                          matTooltip="Eliminar proceso"
                          [disabled]="executing">
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
            </table>
          </mat-card>

          <div class="empty-state" *ngIf="selectedProcesses.length === 0 && !loading">
            <mat-icon class="empty-icon">assignment</mat-icon>
            <span>No hay procesos registrados</span>
          </div>

          <div class="loading-container" *ngIf="loading">
            <mat-icon class="loading-icon">hourglass_empty</mat-icon>
            <span>Cargando...</span>
          </div>
        </mat-tab>

        <mat-tab label="Por Mes/Año">
          <div class="month-filter">
            <mat-form-field appearance="outline">
              <mat-label>Mes</mat-label>
              <mat-select [(value)]="selectedMonth" (selectionChange)="loadProcessesByMonth()">
                <mat-option [value]="0">Todos</mat-option>
                <mat-option *ngFor="let month of months" [value]="month.value">
                  {{ month.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Año</mat-label>
              <mat-select [(value)]="selectedYear" (selectionChange)="loadProcessesByMonth()">
                <mat-option [value]="0">Todos</mat-option>
                <mat-option *ngFor="let year of years" [value]="year">{{ year }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <mat-card class="table-container" *ngIf="selectedProcesses.length > 0">
            <table mat-table [dataSource]="selectedProcesses" class="processes-table">
              <ng-container matColumnDef="pageName">
                <th mat-header-cell *matHeaderCellDef>Página</th>
                <td mat-cell *matCellDef="let process">
                  <div class="page-cell">
                    <mat-icon>language</mat-icon>
                    <span>{{ process.pageName }}</span>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="month">
                <th mat-header-cell *matHeaderCellDef>Mes/Año</th>
                <td mat-cell *matCellDef="let process">
                  <span class="month-badge">{{ getMonthName(process.mes) }} {{ process.anio }}</span>
                </td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Estado</th>
                <td mat-cell *matCellDef="let process">
                  <span class="status-badge" [ngClass]="getStatusClass(process.status)">
                    {{ getStatusLabel(process.status) }}
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="processType">
                <th mat-header-cell *matHeaderCellDef>Tipo</th>
                <td mat-cell *matCellDef="let process">
                  <span class="type-badge">{{ getProcessTypeLabel(process.processType) }}</span>
                </td>
              </ng-container>

              <ng-container matColumnDef="createdAt">
                <th mat-header-cell *matHeaderCellDef>Creado</th>
                <td mat-cell *matCellDef="let process">
                  {{ process.createdAt | date: 'dd/MM/yyyy HH:mm' }}
                </td>
              </ng-container>

              <ng-container matColumnDef="updatedAt">
                <th mat-header-cell *matHeaderCellDef>Actualizado</th>
                <td mat-cell *matCellDef="let process">
                  {{ process.updatedAt | date: 'dd/MM/yyyy HH:mm' }}
                </td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>Acciones</th>
                <td mat-cell *matCellDef="let process">
                  <button mat-icon-button (click)="viewAuditResult(process)"
                          matTooltip="Ver reporte"
                          [disabled]="process.status !== 3 || !process.result">
                    <mat-icon>visibility</mat-icon>
                  </button>
                  <button mat-icon-button (click)="sendAuditReport(process)"
                          matTooltip="Enviar por correo"
                          [disabled]="process.status !== 3 || !process.result || sendingEmail">
                    <mat-icon>mail</mat-icon>
                  </button>
                  <button mat-icon-button (click)="deleteProcess(process)"
                          matTooltip="Eliminar proceso"
                          [disabled]="executing">
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
            </table>
          </mat-card>

          <div class="empty-state" *ngIf="selectedProcesses.length === 0 && !loading">
            <mat-icon class="empty-icon">assignment</mat-icon>
            <span>No hay procesos registrados para el periodo seleccionado</span>
          </div>

          <div class="loading-container" *ngIf="loading">
            <mat-icon class="loading-icon">hourglass_empty</mat-icon>
            <span>Cargando...</span>
          </div>
        </mat-tab>
      </mat-tab-group>
    </div>
  `,
  styles: [`
    .processes-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
      }

      h1 {
        font-family: var(--font-display);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-primary);
        margin: 0;
      }

      .header-actions {
        display: flex;
        gap: 12px;
      }

      button {
        display: flex;
        align-items: center;
        gap: 8px;

        .button-text {
          margin-left: 4px;
        }
      }
    }

    .table-container {
      margin-top: 16px;
      border-radius: var(--radius-lg);
      border: 1px solid rgba(0, 0, 0, 0.05);
      box-shadow: var(--shadow-sm);
    }

    .processes-table {
      width: 100%;
      overflow-x: auto;
    }

    @media (max-width: 768px) {
      .month-filter {
        flex-direction: column;
        gap: 8px;

        mat-form-field {
          width: 100%;
        }
      }

      .header-content {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;

        .header-actions {
          flex-wrap: wrap;
        }
      }
    }

    .page-cell {
      display: flex;
      align-items: center;
      gap: 12px;

      mat-icon {
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(183, 28, 28, 0.1);
        color: var(--primary-color);
      }
    }

    .month-badge {
      background: rgba(183, 28, 28, 0.1);
      color: var(--primary-color);
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
    }

    .status-badge {
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;

      &.pending {
        background: rgba(255, 152, 0, 0.1);
        color: #e65100;
      }

      &.in-progress {
        background: rgba(33, 150, 243, 0.12);
        color: #1565c0;
      }

      &.completed {
        background: rgba(76, 175, 80, 0.1);
        color: #2e7d32;
      }

      &.failed {
        background: rgba(244, 67, 54, 0.1);
        color: #c62828;
      }

      &.cancelled {
        background: rgba(158, 158, 158, 0.12);
        color: #616161;
      }
    }

    .type-badge {
      background: rgba(158, 158, 158, 0.1);
      color: #616161;
      padding: 4px 8px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;
    }

    .month-filter {
      display: flex;
      gap: 16px;
      margin-bottom: 16px;

      mat-form-field {
        width: 200px;
      }
    }

    .empty-state,
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      gap: 16px;
      color: rgba(0, 0, 0, 0.54);

      .empty-icon,
      .loading-icon {
        font-size: 64px;
        color: rgba(0, 0, 0, 0.38);
      }

      .loading-icon {
        animation: spin 1s linear infinite;
      }

      span {
        font-size: 14px;
      }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    mat-tab-group {
      margin-top: 24px;
    }
  `]
})
export class PageProcessesComponent implements OnInit, OnDestroy {
  processes: PageProcess[] = [];
  selectedProcesses: PageProcess[] = [];
  loading = false;
  executing = false;
  private pollSubscription?: Subscription;

  selectedTab = 0;
  selectedMonth = new Date().getMonth() + 1;
  selectedYear = new Date().getFullYear();

  months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  displayedColumns = ['pageName', 'month', 'status', 'processType', 'createdAt', 'updatedAt', 'actions'];

  private pageProcessService = inject(PageProcessService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  get isAdmin(): boolean {
    return this.authService.getCurrentUserRole() === 'Admin';
  }

  ngOnInit() {
    this.loadAllProcesses();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  private hasActiveProcesses(processes: PageProcess[]): boolean {
    return processes.some(p => p.status === 1 || p.status === 2);
  }

  private startPollingIfNeeded(processes: PageProcess[]) {
    if (this.hasActiveProcesses(processes)) {
      if (!this.pollSubscription) {
        this.pollSubscription = interval(5000).subscribe(() => {
          if (this.selectedTab === 0) {
            this.refreshAllProcesses();
          } else {
            this.refreshProcessesByMonth();
          }
        });
      }
    } else {
      this.stopPolling();
    }
  }

  private stopPolling() {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }

  loadAllProcesses() {
    this.loading = true;
    this.pageProcessService.getAllProcesses().subscribe({
      next: (processes) => {
        this.processes = processes;
        this.selectedProcesses = processes;
        this.loading = false;
        this.startPollingIfNeeded(processes);
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open('Error al cargar los procesos', 'Reintentar', { duration: 6000 })
          .onAction()
          .subscribe(() => this.loadAllProcesses());
      }
    });
  }

  refreshAllProcesses() {
    this.pageProcessService.getAllProcesses().subscribe({
      next: (processes) => {
        this.processes = processes;
        this.selectedProcesses = processes;
        this.cdr.markForCheck();
        this.startPollingIfNeeded(processes);
      },
      error: () => {
        this.stopPolling();
      }
    });
  }

  onTabChange(index: number) {
    this.selectedTab = index;
    if (index === 0) {
      this.selectedProcesses = this.processes;
    } else {
      this.loadProcessesByMonth();
    }
  }

  loadProcessesByMonth() {
    if (this.selectedMonth === 0 && this.selectedYear === 0) {
      this.selectedProcesses = this.processes;
      return;
    }

    this.loading = true;
    this.pageProcessService.getProcessesByMonth(this.selectedMonth, this.selectedYear).subscribe({
      next: (processes) => {
        this.selectedProcesses = processes;
        this.loading = false;
        this.startPollingIfNeeded(processes);
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open('Error al cargar los procesos del mes', 'Reintentar', { duration: 6000 })
          .onAction()
          .subscribe(() => this.loadProcessesByMonth());
      }
    });
  }

  refreshProcessesByMonth() {
    this.pageProcessService.getProcessesByMonth(this.selectedMonth, this.selectedYear).subscribe({
      next: (processes) => {
        this.selectedProcesses = processes;
        this.cdr.markForCheck();
        this.startPollingIfNeeded(processes);
      },
      error: () => {
        this.stopPolling();
      }
    });
  }

  getMonthName(month: number): string {
    const foundMonth = this.months.find(m => m.value === month);
    return foundMonth ? foundMonth.label : '';
  }

  getStatusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: 'Pendiente',
      1: 'En Cola',
      2: 'En Proceso',
      3: 'Completado',
      4: 'Fallido',
      5: 'Cancelado'
    };
    return labels[status] || 'Desconocido';
  }

  getStatusClass(status: number): string {
    const classes: Record<number, string> = {
      0: 'pending',
      1: 'in-progress',
      2: 'in-progress',
      3: 'completed',
      4: 'failed',
      5: 'cancelled'
    };
    return classes[status] || '';
  }

  getProcessTypeLabel(type: number): string {
    const labels: Record<number, string> = {
      0: 'Auditoría',
      1: 'Mantenimiento',
      2: 'Verificación'
    };
    return labels[type] || 'Otro';
  }

  viewAuditResult(process: PageProcess) {
    if (process.status !== 3 || !process.result) {
      this.snackBar.open('No hay resultados disponibles para este proceso', 'Cerrar', { duration: 3000 });
      return;
    }
    this.pageProcessService.getAuditReportBlob(process.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
      error: () => {
        this.snackBar.open('Error al generar el reporte', 'Cerrar', { duration: 4000 });
      }
    });
  }

  sendingEmail = false;

  sendAuditReport(process: PageProcess) {
    this.sendingEmail = true;
    this.pageProcessService.sendAuditReport(process.id).subscribe({
      next: (result) => {
        this.sendingEmail = false;
        const message = result?.message || 'Reporte enviado por correo';
        this.snackBar.open(message, 'Cerrar', { duration: 5000 });
      },
      error: (err) => {
        this.sendingEmail = false;
        const message = err?.error?.message || 'Error al enviar el reporte';
        this.snackBar.open(message, 'Cerrar', { duration: 5000 });
      }
    });
  }

  deleteProcess(process: PageProcess) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Proceso',
        message: `¿Estás seguro de que deseas eliminar el proceso de ${process.pageName}?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        type: 'danger',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      this.executing = true;
      this.pageProcessService.deleteProcess(process.id).subscribe({
        next: () => {
          this.processes = this.processes.filter(p => p.id !== process.id);
          this.selectedProcesses = this.selectedProcesses.filter(p => p.id !== process.id);
          this.cdr.markForCheck();
          this.snackBar.open('Proceso eliminado correctamente', 'Cerrar', { duration: 3000 });
          this.executing = false;
        },
        error: () => {
          this.snackBar.open('Error al eliminar el proceso', 'Cerrar', { duration: 3000 });
          this.executing = false;
        }
      });
    });
  }

  executeAllAudits() {
    this.executing = true;
    this.pageProcessService.executeAllAudits().subscribe({
      next: (result) => {
        if (result.success) {
          this.snackBar.open(result.message, 'Cerrar', { duration: 5000 });
          this.loadAllProcesses();
        } else {
          this.snackBar.open(result.message, 'Cerrar', { duration: 5000 });
        }
        this.executing = false;
      },
      error: () => {
        this.snackBar.open('Error al ejecutar las auditorías', 'Cerrar', { duration: 5000 });
        this.executing = false;
      }
    });
  }

  createMonthlyAudits() {
    this.executing = true;
    this.pageProcessService.createMonthlyAudits().subscribe({
      next: (result) => {
        if (result.success) {
          const message = `${result.message}. Procesos creados: ${result.processesCreated}, Omitidos: ${result.processesSkipped}`;
          this.snackBar.open(message, 'Cerrar', { duration: 5000 });
          this.loadAllProcesses();
        } else {
          this.snackBar.open(result.message, 'Cerrar', { duration: 5000 });
        }
        this.executing = false;
      },
      error: () => {
        this.snackBar.open('Error al crear las auditorías mensuales', 'Cerrar', { duration: 5000 });
        this.executing = false;
      }
    });
  }
}
